import { Box, Flex, Text } from "@chakra-ui/react";
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { ViewState } from "react-map-gl";
import { Route, useNavigate, useParams } from "react-router-dom";
import { Coordinate, SpotStat } from "~/generated/graphql.types";
import { Map } from "./parts/Map";
import {
  SpotForPreviewSidebarFragment,
  useFetchCourseQuery,
  useRoutingInfomationQuery,
} from "./index.generated";
import { Sidebar } from "./parts/Sidebar";
import { DraggableStreetViewViewer } from "./parts/StreetView/DraggableStreetViewViewer";
import { RouteUsageGuide } from "./parts/routeUsageGuide/RouteUsageGuide";

const Inner = () => {
  const navigate = useNavigate();
  const params = useParams<{ courseId: string }>();
  const courseId = params.courseId!;
  const { isOpenStreetViewViewer } = useContext(EditModeContext);

  const [fetchCourseRes] = useFetchCourseQuery({
    variables: { id: courseId },
    requestPolicy: "network-only",
  });

  const [routingInfomationRes] = useRoutingInfomationQuery({
    variables: { courseId: courseId },
  });

  if (fetchCourseRes.error?.message != undefined) {
    alert(fetchCourseRes.error?.message);
    navigate("/");
  }

  const course = fetchCourseRes.data!.course!;
  course.spots = course.spots.sort((a, b) => (a.order > b.order ? 1 : -1));
  const start: SpotForPreviewSidebarFragment | undefined = useMemo(() => {
    if (course.spots.length < 1) return undefined;
    return course.spots[0];
  }, [course.spots]);
  const dest: SpotForPreviewSidebarFragment | undefined = useMemo(() => {
    if (course.spots.length < 2) return undefined;
    return course.spots[course.spots.length - 1];
  }, [course.spots]);
  const waypoints = useMemo(() => course.spots.slice(1, -1), [course.spots]);

  const [mapViewState, setMapViewState] = useState<Partial<ViewState>>({
    latitude: start ? Number(start.coordinate.lat) : 35.66708703846561,
    longitude: start ? Number(start.coordinate.lon) : 139.69350241388196,
    zoom: 13,
  });

  const focusOnSpot = useCallback(
    (coordinate: Coordinate) =>
      setMapViewState({
        ...mapViewState,
        latitude: coordinate.lat,
        longitude: coordinate.lon,
      }),
    []
  );

  const [clickedLocation, setClickLocation] = useState<Coordinate>();

  return (
    <>
      <Flex overflow="hidden">
        <Box width="500px" height="100vh">
          <Sidebar
            courseName={course.name}
            courseId={course.id}
            start={start}
            dest={dest}
            waypoints={waypoints}
            onClickSpot={focusOnSpot}
            isVisibleToAllUserGroups={course.isVisibleToAllUserGroups}
            statsAllSpots={
              routingInfomationRes.data?.routingInfomation.routingInfomation
                ?.spotStat
            }
            statsWithoutOpt={
              routingInfomationRes.data?.routingInfomation
                .routingInfomationWithoutOpt?.spotStat
            }
          />
        </Box>
        <Box>
          <Map
            courseId={course.id}
            viewState={mapViewState}
            onChangeViewState={setMapViewState}
            start={start}
            dest={dest}
            waypoints={waypoints}
            style={{
              width: "calc(100vw - 500px)",
              height: "100%",
              position: "fixed",
              color: "white",
            }}
            focusOnSpot={focusOnSpot}
            onClickLocation={(clickedCoordinate) =>
              setClickLocation(clickedCoordinate)
            }
            routeAllSpots={
              routingInfomationRes.data?.routingInfomation.routingInfomation
                ?.route
            }
            routeWithoutOpt={
              routingInfomationRes.data?.routingInfomation
                .routingInfomationWithoutOpt?.route
            }
            navigationPoints={
              routingInfomationRes.data?.routingInfomation.routingInfomation
                ?.navigation
            }
          />
        </Box>
        {isOpenStreetViewViewer ? (
          <DraggableStreetViewViewer clickedCoordinate={clickedLocation} />
        ) : null}
      </Flex>
      <Box position="absolute" top={2.5} left={505}>
        <RouteUsageGuide />
      </Box>
    </>
  );
};

export enum ViewMode {
  spotsView,
  voiceView,
}

export const EditModeContext = createContext<{
  isOpenStreetViewViewer: boolean;
  viewMode: ViewMode;
  spotIdForEdit?: string;
  switchStreetViewViewer: (status: boolean) => void;
  switchViewMode: (mode: ViewMode) => void;
  setSpotIdForEdit: (spotId?: string) => void;
}>({
  isOpenStreetViewViewer: false,
  viewMode: ViewMode.spotsView,
  switchStreetViewViewer: (status) => {},
  switchViewMode: (mode) => {},
  setSpotIdForEdit: () => {},
});

export const CourseView = () => {
  const [spotIdForEdit, setSpotIdForEdit] = useState<string | undefined>(
    undefined
  );
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.spotsView);
  const switchViewMode = useCallback((mode: ViewMode) => setViewMode(mode), []);

  const [isOpenStreetViewViewer, setIsOpenStreetViewViewer] =
    useState<boolean>(false);
  const switchStreetViewViewer = useCallback(
    (status: boolean) => setIsOpenStreetViewViewer(status),
    []
  );

  return (
    <EditModeContext.Provider
      value={{
        isOpenStreetViewViewer,
        viewMode,
        spotIdForEdit,
        switchStreetViewViewer,
        switchViewMode,
        setSpotIdForEdit,
      }}
    >
      <Suspense fallback={<Text>Loading...</Text>}>
        <Inner />
      </Suspense>
    </EditModeContext.Provider>
  );
};
